import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
  Grid,
  Avatar,
  Stack,
} from '@mui/material';
import {
  Search,
  Download,
  Assessment,
  Star,
  CheckCircle,
  Cancel,
  Pending,
  DriveEta,
} from '@mui/icons-material';
import { driverEarningsService, Driver, DriverEarningsFilters } from '@/services/driverEarningsService';
import { DriverDetailedEarningsModal } from '@/components/driver/DriverDetailedEarningsModal';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
};

const getStatusColor = (status: string): 'success' | 'default' | 'error' | 'warning' => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'inactive':
    case 'offline':
      return 'default';
    case 'suspended':
      return 'error';
    default:
      return 'default';
  }
};

const getApplicationStatusColor = (status: string): 'success' | 'default' | 'error' | 'warning' => {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'success';
    case 'pending':
    case 'under_review':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

export const DriverEarningsBreakdown: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'OFFLINE'>('all');
  const [applicationStatusFilter, setApplicationStatusFilter] = useState<'all' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'UNDER_REVIEW'>('all');
  const [cityFilter, setCityFilter] = useState('');

  // Detailed earnings modal
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [detailedEarningsModalOpen, setDetailedEarningsModalOpen] = useState(false);

  useEffect(() => {
    fetchDrivers();
  }, [page, rowsPerPage, statusFilter]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: DriverEarningsFilters = {
        page: page + 1, // Backend uses 1-based pagination
        limit: rowsPerPage,
        status: statusFilter !== 'all' ? (statusFilter.toLowerCase() as any) : undefined,
      };

      const response = await driverEarningsService.getDrivers(filters);
      setDrivers(response.data.drivers);
      setTotalCount(response.data.pagination.total);
    } catch (err: any) {
      console.error('Error fetching drivers:', err);
      setError(err.response?.data?.message || 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page on search
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleApplicationStatusFilterChange = (event: any) => {
    setApplicationStatusFilter(event.target.value);
    setPage(0);
  };

  const handleCityFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCityFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const filters: DriverEarningsFilters = {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        applicationStatus: applicationStatusFilter !== 'all' ? applicationStatusFilter : undefined,
        city: cityFilter || undefined,
      };
      const blob = await driverEarningsService.exportDriverEarnings(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `driver-earnings-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetailedEarnings = (driver: Driver) => {
    setSelectedDriver(driver);
    setDetailedEarningsModalOpen(true);
  };

  const handleCloseDetailedEarningsModal = () => {
    setDetailedEarningsModalOpen(false);
    setSelectedDriver(null);
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Driver Earnings Breakdown
            </Typography>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={16} /> : <Download />}
              onClick={handleExportCSV}
              disabled={exporting || loading}
              size="small"
            >
              Export CSV
            </Button>
          </Box>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, phone..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status">
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                  <MenuItem value="OFFLINE">Offline</MenuItem>
                  <MenuItem value="SUSPENDED">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Application</InputLabel>
                <Select
                  value={applicationStatusFilter}
                  onChange={handleApplicationStatusFilterChange}
                  label="Application"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="City"
                value={cityFilter}
                onChange={handleCityFilterChange}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" gap={1} alignItems="center" height="100%">
                <Typography variant="caption" color="text.secondary">
                  {totalCount} driver{totalCount !== 1 ? 's' : ''} found
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Driver</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell align="right">Total Earnings</TableCell>
                      <TableCell align="center">Trips</TableCell>
                      <TableCell align="center">Rating</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Application</TableCell>
                      <TableCell align="center">Verified</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {drivers.length > 0 ? (
                      drivers.map((driver) => (
                        <TableRow key={driver.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                                <DriveEta fontSize="small" />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {driver.name || `${driver.firstName} ${driver.lastName}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {driver.driverId || driver.id.substring(0, 8)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="body2">{driver.email}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {driver.phone}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{driver.serviceLocations?.[0] || '-'}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              N/A
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">N/A</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                              <Star fontSize="small" sx={{ color: 'warning.main' }} />
                              <Typography variant="body2">{driver.rating?.toFixed(1) || '0.0'}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={driver.status}
                              size="small"
                              color={getStatusColor(driver.status)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={driver.applicationStatus}
                              size="small"
                              color={getApplicationStatusColor(driver.applicationStatus)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {driver.completionPercentage === 100 ? (
                              <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                            ) : (
                              <Cancel fontSize="small" sx={{ color: 'error.main' }} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Detailed Earnings Breakdown">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetailedEarnings(driver)}
                                color="primary"
                              >
                                <Assessment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography variant="body2" color="text.secondary" py={4}>
                            No drivers found matching your filters
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Detailed Earnings Modal */}
      {selectedDriver && (
        <DriverDetailedEarningsModal
          open={detailedEarningsModalOpen}
          onClose={handleCloseDetailedEarningsModal}
          driverId={selectedDriver.id}
          driverName={selectedDriver.fullName}
        />
      )}
    </Box>
  );
};
